import type { IAppRoute, IAppLocationItem } from '../elements/interfaces/applocation.js';
import type { DeesAppui } from '../elements/00group-appui/dees-appui/dees-appui.js';
import type { IMenuItem } from '../elements/interfaces/tab.js';
import type { ISecondaryMenuGroup } from '../elements/interfaces/secondarymenu.js';
import { DemoStorageDataProvider } from './storage.js';
import { chartSamples } from './charts.js';
import { operationMetrics, operationStatus, type TDemoHealth } from './operations.js';

export type TGatewayLayout = 'browse' | 'inspect' | 'focus';
export type TGatewayView = 'overview' | 'services' | 'analytics' | 'files' | 'settings';
export interface IGatewayService { id: string; name: string; region: string; kind: string; latency: number; status: string; }
export const gatewayViews = [
  { id: 'overview', name: 'Overview', iconName: 'lucide:LayoutDashboard' },
  { id: 'services', route: 'services/:service?', name: 'Services', iconName: 'lucide:Server' },
  { id: 'analytics', route: 'analytics/:tab?', name: 'Analytics', iconName: 'lucide:ChartNoAxesCombined' },
  { id: 'files', name: 'Files', iconName: 'lucide:Folder' },
  { id: 'settings', route: 'settings/:section?', name: 'Settings', iconName: 'lucide:Settings' },
] as const;
const regions: Record<string, string> = { europe: 'Europe', 'north-america': 'North America', 'asia-pacific': 'Asia Pacific' };
export const validateGatewayRoute = (route: IAppRoute): boolean => {
  const { viewId, params, query } = route;
  if (viewId === 'services') return (!params.service || ['edge-eu', 'edge-us', 'edge-ap', 'worker-eu'].includes(params.service)) && Object.keys(query).every(key => ['region', 'attention'].includes(key)) && (!query.region || Object.hasOwn(regions, query.region)) && (!query.attention || query.attention === '1');
  if (viewId === 'settings') return (!params.section || ['general', 'notifications'].includes(params.section)) && !Object.keys(query).length;
  if (viewId === 'analytics') return (!params.tab || ['traffic', 'regions', 'capacity'].includes(params.tab)) && !Object.keys(query).length;
  if (viewId === 'files') return Object.keys(query).every(key => ['prefix', 'key'].includes(key));
  return !Object.keys(query).length;
};
export const gatewayLayouts = [{ key: 'browse', label: 'Browse' }, { key: 'inspect', label: 'Inspect' }, { key: 'focus', label: 'Focus' }];

/** One disposable model per demo instance; cached views share it, no global state. */
export class GatewayDemoState extends EventTarget {
  public layout: TGatewayLayout = 'browse';
  public view: TGatewayView = 'overview';
  public health: TDemoHealth = 'healthy';
  public sample = 0;
  public sampledAt = Date.now();
  public region = 'All regions';
  public attentionOnly = false;
  public serviceId = '';
  public analyticsTab = 'traffic';
  public settingsSection = 'General';
  public fileLocation = { prefix: '', key: '' };
  public displayName = 'Alex Morgan';
  public workspaceName = 'Gateway';
  public notifications = true;
  public busy = false;
  public readonly files: DemoStorageDataProvider;
  private openedServices: string[] = [];
  public readonly analyticsTabs: IMenuItem[];

  constructor(public readonly app: DeesAppui) {
    super();
    this.files = new DemoStorageDataProvider([
      { key: 'gateway.json', contentType: 'application/json', content: '{\n  "service": "edge-eu",\n  "region": "Europe",\n  "connectionPool": 100,\n  "latencyTargetMs": 200\n}' },
      { key: 'runbooks/latency.md', contentType: 'text/markdown', content: '# Elevated latency\n\n1. Inspect edge-eu in Services.\n2. Review database connections and the 200 ms target.\n3. Restore service in the example to complete the incident.\n\nConfiguration edits are drafts; they do not deploy changes.' },
      { key: 'reports/health.md', contentType: 'text/markdown', content: '# Gateway health report\n\nThree regions. Four services.\n\nService targets: 99.9% successful requests, p95 latency below 200 ms.' },
      { key: 'assets/gateway.svg', contentType: 'image/svg+xml', content: '<svg xmlns="http://www.w3.org/2000/svg" width="600" height="360"><rect width="600" height="360" rx="32" fill="#152238"/><circle cx="450" cy="180" r="110" fill="#007aff"/><text x="40" y="190" fill="white" font-size="40" font-family="sans-serif">Gateway</text></svg>' },
    ], message => this.record(message, 'update'));
    this.analyticsTabs = [
      { key: 'traffic', label: 'Traffic' }, { key: 'regions', label: 'Regions' }, { key: 'capacity', label: 'Capacity' },
    ].map(tab => ({ ...tab, action: () => { void this.app.navigateToView('analytics', { tab: tab.key }); } }));
  }

  public get needsAttention() { return this.health === 'degraded' || this.health === 'incident'; }
  public get metrics() { return operationMetrics(this.health, this.sample); }
  public get status() { return operationStatus(this.health, this.sample, this.sampledAt); }
  public get charts() {
    const data = chartSamples(this.sample, this.sampledAt);
    return { ...data, timeline: [{ name: 'Requests / min', data: [11240, 11820, 11600, 12100, 12640, 12300, 12480].map((value, index) => ({ x: new Date(this.sampledAt - (6 - index) * 60000).toISOString(), y: value + this.sample * 120 })) }] };
  }
  public get services(): IGatewayService[] {
    return [
      { id: 'edge-eu', name: 'API gateway', region: 'Europe', kind: 'Gateway', latency: Number(this.metrics[1].value), status: this.needsAttention ? 'Needs attention' : 'Healthy' },
      { id: 'edge-us', name: 'API gateway', region: 'North America', kind: 'Gateway', latency: 94, status: 'Healthy' },
      { id: 'edge-ap', name: 'API gateway', region: 'Asia Pacific', kind: 'Gateway', latency: 108, status: 'Healthy' },
      { id: 'worker-eu', name: 'Background worker', region: 'Europe', kind: 'Worker', latency: 42, status: 'Healthy' },
    ];
  }
  public get selectedService() { return this.services.find(service => service.id === this.serviceId); }
  public get visibleServices() { return this.services.filter(service => (this.region === 'All regions' || service.region === this.region) && (!this.attentionOnly || service.status === 'Needs attention')); }

  public record(message: string, type: 'update' | 'custom' = 'custom') {
    this.app.activityLog.add({ type, user: this.displayName, message });
  }
  public refresh() { this.sample++; this.sampledAt = Date.now(); this.record(`Health checks completed · sample ${this.sample + 1}`); this.changed(); }
  public setHealth(health: TDemoHealth) {
    this.health = health; this.sample++; this.sampledAt = Date.now();
    this.record(this.needsAttention ? 'edge-eu exceeded the 200 ms latency target. Database connections need attention.' : 'edge-eu recovered. Response times and database connections are within target.');
    this.changed();
  }
  private serviceQuery(region = this.region, attention = this.attentionOnly): Record<string, string> {
    const key = Object.keys(regions).find(key => regions[key] === region);
    return { ...(key ? { region: key } : {}), ...(attention ? { attention: '1' } : {}) };
  }
  public async openService(id: string) {
    if (!this.services.some(service => service.id === id)) return false;
    const opened = await this.app.navigateToView('services', { service: id }, this.serviceQuery());
    if (opened) this.record(`Opened ${id} for inspection`);
    return opened;
  }
  public async closeService(id: string) {
    const index = this.openedServices.indexOf(id);
    const remaining = this.openedServices.filter(key => key !== id);
    if (this.serviceId === id && !await this.app.navigateToView('services', { service: remaining[Math.max(0, index - 1)] || '' }, this.serviceQuery())) return;
    this.openedServices = remaining;
    this.changed();
  }
  public closeOtherServices() { this.openedServices = this.serviceId ? [this.serviceId] : []; this.changed(); }
  public async navigate(view: TGatewayView) { return this.app.navigateToView(view); }
  public setLayout(layout: TGatewayLayout) {
    this.layout = layout;
    this.app.setMainMenuCollapsed(layout !== 'browse');
    this.app.setSecondaryMenuCollapsed(layout === 'focus');
    this.app.setActivityLogVisible(layout === 'inspect');
    this.app.setBottomBarVisible(layout !== 'focus');
    this.changed();
  }
  public activate(route: IAppRoute) {
    this.view = route.viewId as TGatewayView;
    if (this.view === 'services') {
      this.serviceId = route.params.service || '';
      this.region = regions[route.query.region] || 'All regions';
      this.attentionOnly = route.query.attention === '1';
      if (this.serviceId && !this.openedServices.includes(this.serviceId)) this.openedServices.push(this.serviceId);
    }
    if (this.view === 'files') this.fileLocation = { prefix: route.query.prefix || '', key: route.query.key || '' };
    if (this.view === 'settings') this.settingsSection = route.params.section === 'notifications' ? 'Notifications' : 'General';
    if (this.view === 'analytics') this.analyticsTab = route.params.tab || 'traffic';
    this.syncChrome();
  }
  public changed() { this.syncChrome(); this.dispatchEvent(new Event('change')); }

  public syncChrome() {
    const app = this.app;
    const title = gatewayViews.find(view => view.id === this.view)!.name;
    const items: IAppLocationItem[] = [{ label: this.workspaceName, href: app.createViewUrl('overview') }, { label: title }];
    const detail = this.view === 'services' ? this.serviceId : this.view === 'settings' ? this.settingsSection : this.view === 'analytics' ? this.analyticsTab[0].toUpperCase() + this.analyticsTab.slice(1) : '';
    if (detail) { items[1].href = app.createViewUrl(this.view, {}, this.view === 'services' ? this.serviceQuery() : {}); items.push({ label: detail }); }
    if (this.view === 'files' && (this.fileLocation.key || this.fileLocation.prefix)) {
      items[1].href = app.createViewUrl('files');
      items.push({ label: this.fileLocation.key || this.fileLocation.prefix });
    }
    app.setLocationBreadcrumbs(items);
    app.setBreadcrumbs(items.map(item => item.label));
    app.setUser({ name: this.displayName, email: 'alex@gateway.example', status: this.busy ? 'busy' : 'online' });
    app.setMainMenu({ logoText: this.workspaceName });
    if (this.needsAttention) app.setMainMenuBadge('services', 1); else app.clearMainMenuBadge('services');
    app.bottomBar.updateWidget('health', { label: this.needsAttention ? '1 service needs attention' : 'All services healthy', status: this.needsAttention ? 'warning' : 'success' });
    app.bottomBar.updateWidget('sample', { label: `Sample ${this.sample + 1}` });
    let groups: ISecondaryMenuGroup[] = [];
    if (this.view === 'services') {
      groups = [{ name: 'Regions', items: ['All regions', 'Europe', 'North America', 'Asia Pacific'].map(region => ({
        key: region, iconName: 'lucide:Globe', badge: region === 'Europe' && this.needsAttention ? 1 : undefined,
        badgeVariant: 'warning' as const, action: () => { void app.navigateToView('services', {}, this.serviceQuery(region)); },
      })) }, { name: 'Review', items: [
        { key: 'Needs attention', type: 'filter', active: this.attentionOnly, onToggle: active => { void app.navigateToView('services', {}, this.serviceQuery(this.region, active)); } },
        { key: 'Run health check', type: 'action', iconName: 'lucide:RefreshCw', action: () => this.refresh() },
      ] }];
      // The tab bar holds the open services: each one is added by key, so a
      // service that is opened again never becomes a twin, and the attention
      // badge is a partial update rather than a rewritten list.
      app.setContentTabs([{ key: 'services', label: 'All services', action: () => { void app.navigateToView('services', {}, this.serviceQuery()); } }]);
      for (const id of this.openedServices) {
        app.addContentTab({ key: id, label: id, closeable: true, action: () => { void this.openService(id); }, onClose: () => this.closeService(id) });
      }
      for (const service of this.services) {
        app.updateContentTab(service.id, service.status === 'Needs attention'
          ? { badge: 1, badgeVariant: 'warning' } : { badge: undefined });
      }
      app.selectContentTab(this.serviceId || 'services');
    } else if (this.view === 'analytics') {
      app.setContentTabs(this.analyticsTabs); app.selectContentTab(this.analyticsTab);
    } else if (this.view === 'settings') {
      groups = [{ name: 'Workspace', items: ['General', 'Notifications'].map(section => ({ key: section, iconName: section === 'General' ? 'lucide:Settings' : 'lucide:Bell', action: () => { void app.navigateToView('settings', { section: section.toLowerCase() }); } })) }];
    }
    app.setSecondaryMenu({ heading: this.view === 'settings' ? 'Settings' : 'Services', groups });
    app.setSecondaryMenuVisible(groups.length > 0);
    app.setSecondaryMenuSelection(this.view === 'settings' ? this.settingsSection : this.region);
    const hasTabs = this.view === 'services' || this.view === 'analytics';
    if (!hasTabs) app.setContentTabs([]);
    app.setContentTabsVisible(hasTabs);
    app.setContentTabsAutoHide(this.layout === 'focus', 1);
    app.setContentTabActionsRight(app.getContentTabs().filter(tab => tab.closeable).length > 1 ? [
      { id: 'close-others', iconName: 'lucide:X', tooltip: 'Close other service tabs', action: () => this.closeOtherServices() },
    ] : []);
  }
}
